import * as React from "react";
import { createContext, ReactNode, useContext, useEffect, useReducer, useRef, useState } from "react";
import { isMobileSafari, useMobileOrientation } from "react-device-detect";
import themeSet from "../../themes/Default";
import { ThemeMode, ThemeSet } from "../../themes/ThemeSet";
import {isMobile, isTablet} from 'react-device-detect';
import { IStorage } from "../../services/storage/storage-service/StorageService";
import { StorageKeys } from "../../services/storage/StorageKeys";
const appSettingsVersion: number = 2;


export type AppSettings = {
    version: number;
    themeSet: ThemeSet;
    maximizeContent: boolean;
    sideSheetOpen: boolean;
};

export enum AppSettingsActionTypeEnum {
    ThemeSet = "themeSet",
    ThemeSetMode = "themeSet.mode",
    //Authenticated = "userState.authenticated",
    //Customer = "userState.customer",
    //User = "userState.user",
    //UserState = "userState",
    //ResetUserState = "resetUserState",
    MaximizeContent = 'maximizeContent',
    SideSheetOpenChanged = "SIDE_SHEET_OPEN_CHANGED"
}

export type ThemeSetAction = {
    type: AppSettingsActionTypeEnum.ThemeSet;
    themeSet: ThemeSet;
};

export type ThemeSetModeAction = {
    type: AppSettingsActionTypeEnum.ThemeSetMode;
    mode: ThemeMode;
};

export type MaximizeAction = {
    type: AppSettingsActionTypeEnum.MaximizeContent;
    on: boolean;
};

export type SideSheetOpenChangedAction = {
    type: AppSettingsActionTypeEnum.SideSheetOpenChanged;
    open: boolean;
};

export type AppSettingsAction = ThemeSetAction | ThemeSetModeAction | MaximizeAction | SideSheetOpenChangedAction;

const reducer = (state: AppSettings, action: AppSettingsAction): AppSettings => {
    switch (action.type) {
        case AppSettingsActionTypeEnum.ThemeSet:
            return {
                ...state,
                themeSet: action.themeSet
            };
        case AppSettingsActionTypeEnum.ThemeSetMode:
            return {
                ...state,
                themeSet: {
                    ...state.themeSet,
                    mode: action.mode
                }
            };

        case AppSettingsActionTypeEnum.MaximizeContent:
            return {
                ...state,
                maximizeContent: action.on
            }

        case AppSettingsActionTypeEnum.SideSheetOpenChanged:
            return {
                ...state,
                sideSheetOpen: action.open
            }
    

        default:
            return state
    }
};

export type AppContextProviderProps = { 
    children: ReactNode, 
    storageProvider: IStorage
};

export const defaultAppSettings: AppSettings = { 
    version: appSettingsVersion, 
    themeSet: themeSet,
    maximizeContent: false,
    sideSheetOpen: false
};

const AppContext = createContext<[AppSettings, (action: AppSettingsAction) => void] | undefined>(undefined);

function storeState(storage: IStorage, settings: AppSettings): void {
    storage.set(StorageKeys.AppSettings, settings);
}

export function AppContextProvider({ children, storageProvider }: AppContextProviderProps) {

    const isPhone = isMobile && !isTablet;
    const { isLandscape } = useMobileOrientation();

    const portraitRef = useRef<MediaQueryList>(undefined);
        
    let currentAppSettings = storageProvider.get<AppSettings>(StorageKeys.AppSettings, defaultAppSettings);

    if (currentAppSettings.version === 1) {
        // Convert appSettings to version 2
        currentAppSettings = {
            ...defaultAppSettings,
            themeSet: {
                ...defaultAppSettings.themeSet,
                mode: currentAppSettings.themeSet.mode
            }
        };
    }

    // refresh the theme set to ensure we pick up any changes
    currentAppSettings = {
        ...currentAppSettings,
        themeSet: {
            ...themeSet,
            mode: currentAppSettings.themeSet.mode
        }
    };

    storeState(storageProvider, currentAppSettings);
    
    const [appSettings, dispatchAppSettings] = useReducer(reducer, currentAppSettings);

    // this is internal to the context and not to be available for any outside
    const [autoMaximizeContent, setAutoMaximizeContent] = useState(false);

    const onMediaQueryChange = (e: MediaQueryListEvent) => {

        //console.log(`media query match: ${e.matches}`);

        if(e.matches) {
            // Portrait mode
            //if (autoMaximizeContent) {
                dispatchAppSettings({
                    type: AppSettingsActionTypeEnum.MaximizeContent,
                    on: false
                });
            //}
    
        } else {
            // Landscape
            //setAutoMaximizeContent(true);            
            //if (!appSettings.maximizeContent) {
                dispatchAppSettings({
                    type: AppSettingsActionTypeEnum.MaximizeContent,
                    on: true
                });
            //}
        }
    };

    // Whenever appSettings changes store it in storage
    useEffect(() => {

        storeState(storageProvider, appSettings);

    }, [appSettings, storageProvider]);

    useEffect(() => {

        if (isMobileSafari && !portraitRef.current) {
            // workaround for iphone, running from home screen.  Does not detect window resize
            // consistently in react-device-detect
            portraitRef.current = window.matchMedia("(orientation: portrait)");
            if (portraitRef.current) {
                portraitRef.current.addEventListener("change", onMediaQueryChange);
            }
        }
    });

    useEffect(() => {

        if (!isMobileSafari) {

            if (isPhone && isLandscape) {

                if (!autoMaximizeContent) {
                    setAutoMaximizeContent(true);            
                    if (!appSettings.maximizeContent) {
                        dispatchAppSettings({
                            type: AppSettingsActionTypeEnum.MaximizeContent,
                            on: true
                        });
                    }
                }
            } else {
                if (autoMaximizeContent) {
                    setAutoMaximizeContent(false);
                    dispatchAppSettings({
                        type: AppSettingsActionTypeEnum.MaximizeContent,
                        on: false
                    });
                }
            }
        }
    }, [
        isPhone,
        isLandscape,
        appSettings.maximizeContent,
        dispatchAppSettings,
        autoMaximizeContent,
        setAutoMaximizeContent
    ]);

    return (
        <AppContext.Provider value={[appSettings, dispatchAppSettings]}>
            {children}
        </AppContext.Provider>
    );
}

export function useAppSettings() {
    const context = useContext<[AppSettings, (action: AppSettingsAction) => void] | undefined>(AppContext);
    if (!context) {
        throw new Error("useAppSettings must be used within a AppContextProvider");
    }
    return context
}
